import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import type { AiInterpretation, Submission } from "@/types";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";

interface SubRow {
  id: string;
  userNome: string | null;
  userCognome: string | null;
  userEmail: string | null;
  nomeAnonimo: string | null;
  cognomeAnonimo: string | null;
}

interface AnswerRow {
  id: string;
  questionId: string;
  questionTesto: string;
  questionTipo: "chiusa" | "aperta" | "scala";
  sezione: string | null;
  opzioni: string[] | null;
  rispostaCorretta: number | null;
  rispostaIndice: number | null;
  rispostaTesto: string | null;
  rispostaScala: number | null;
  isCorrect: boolean | null;
  aiInterpretation: AiInterpretation | null;
  aiProcessedAt: string | null;
}

export default function StudentAnswers() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const selectedSubId = params.get("s");

  const [subs, setSubs] = useState<SubRow[]>([]);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [answers, setAnswers] = useState<AnswerRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<SubRow[]>(`/admin/tests/${id}/submissions`)
      .then(setSubs)
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!selectedSubId) { setSubmission(null); setAnswers([]); return; }
    api.get<{ submission: Submission; answers: AnswerRow[] }>(
      `/admin/submissions/${selectedSubId}/answers`,
    )
      .then((r) => { setSubmission(r.submission); setAnswers(r.answers); });
  }, [selectedSubId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <header className="flex justify-between items-baseline mb-5">
        <h1 className="text-2xl font-serif text-primary">Risposte studenti</h1>
        <Link to={`/admin/tests/${id}/risultati`} className="font-mono text-xs text-secondary hover:text-primary">← overview</Link>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <aside className="space-y-1 md:max-h-[80vh] md:overflow-y-auto">
          <div className="font-mono text-xs uppercase tracking-wider text-secondary mb-2">
            Studenti ({subs.length})
          </div>
          {subs.map((s) => {
            const nome = s.userEmail
              ? `${s.userNome ?? ""} ${s.userCognome ?? ""}`.trim() || s.userEmail
              : `${s.nomeAnonimo ?? ""} ${s.cognomeAnonimo ?? ""}`.trim() || "Anonimo";
            return (
              <button
                key={s.id}
                onClick={() => setParams({ s: s.id })}
                className={`block w-full text-left px-3 py-2 rounded-lg font-mono text-sm transition-colors
                  ${selectedSubId === s.id
                    ? "bg-accent text-white"
                    : "text-secondary hover:bg-card hover:text-primary"}`}
              >
                {nome}
              </button>
            );
          })}
        </aside>

        <main className="md:col-span-2 space-y-3">
          {!submission ? (
            <Card><p className="text-secondary text-sm">Seleziona uno studente per vedere le sue risposte.</p></Card>
          ) : (
            <>
              {answers.map((a) => (
                <Card key={a.id} className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge color="#2C5F8A">{a.questionTipo}</Badge>
                    {a.sezione && <Badge variant="outline" color="#5B3E8A">{a.sezione}</Badge>}
                    {a.questionTipo === "chiusa" && (
                      a.isCorrect
                        ? <Badge color="#2A7A4B">✓ Corretta</Badge>
                        : <Badge color="#E8613C">✗ Sbagliata</Badge>
                    )}
                  </div>
                  <p className="text-primary font-serif">{a.questionTesto}</p>

                  {a.questionTipo === "chiusa" && a.opzioni && (
                    <ul className="text-sm space-y-1">
                      {a.opzioni.map((op, i) => {
                        const isChosen = i === a.rispostaIndice;
                        const isRight = i === a.rispostaCorretta;
                        return (
                          <li
                            key={i}
                            className={`
                              ${isRight ? "text-green" : isChosen ? "text-red-300" : "text-secondary"}
                              ${isChosen ? "font-semibold" : ""}
                            `}
                          >
                            {String.fromCharCode(65 + i)}. {op}
                            {isChosen && " ← studente"}
                            {isRight && " ✓"}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {a.questionTipo === "aperta" && (
                    <>
                      <blockquote className="border-l-4 border-border pl-3 text-secondary italic text-sm">
                        {a.rispostaTesto ?? "(nessuna risposta)"}
                      </blockquote>
                      {a.aiInterpretation ? (
                        <AiInterpretationCard interp={a.aiInterpretation} />
                      ) : a.rispostaTesto ? (
                        <div className="text-xs text-muted font-mono">⏳ Interpretazione AI in coda…</div>
                      ) : null}
                    </>
                  )}

                  {a.questionTipo === "scala" && (
                    <div className="text-primary font-mono">Valore: {a.rispostaScala ?? "—"}</div>
                  )}
                </Card>
              ))}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function AiInterpretationCard({ interp }: { interp: AiInterpretation }) {
  return (
    <div className="bg-bg border border-purple/40 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-mono uppercase text-xs tracking-wider text-purple">
          🤖 Interpretazione AI
        </div>
        <div className="font-mono text-xs text-secondary">
          Comprensione: <strong className="text-primary">{interp.comprensione}/5</strong>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-green font-mono mb-1">Punti forti</div>
          <ul className="text-secondary list-disc list-inside space-y-0.5">
            {interp.puntiForti.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-accent font-mono mb-1">Lacune</div>
          <ul className="text-secondary list-disc list-inside space-y-0.5">
            {interp.lacune.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-secondary font-mono mb-1">Suggerimento</div>
        <p className="text-primary text-sm">{interp.suggerimento}</p>
      </div>
      <div className="bg-card rounded-lg p-3">
        <div className="text-xs uppercase tracking-wider text-secondary font-mono mb-1">Sintesi (visibile allo studente)</div>
        <p className="text-primary italic">{interp.sintesi}</p>
      </div>
    </div>
  );
}
